import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Container, Row, Col, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { LoadingState } from '../components/PageState';

const SCOPES = ['api://7e9ba977-33b0-4e9c-ae75-55f65da317bd/access'];

// What went wrong with a sign-in, by MSAL error code, or by Microsoft's AADSTS code in the message
const signInMessage = (error) => {
  const code = error?.errorCode || '';
  const detail = `${error?.errorMessage || error?.message || ''}`;
  if (code === 'popup_window_error' || code === 'empty_window_error') {
    return 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.';
  }
  if (code === 'user_cancelled') {
    return 'The sign-in window was closed before you finished. Try again when you are ready.';
  }
  if (code === 'interaction_in_progress') {
    return 'A sign-in window is already open. Finish signing in there, or close it and try again.';
  }
  if (code === 'timed_out' || code === 'monitor_window_timeout') {
    return 'The sign-in window stopped responding. Try again.';
  }
  if (code === 'no_network_connectivity' || code === 'post_request_failed' || code === 'get_request_failed') {
    return 'Could not reach Microsoft. Check your connection and try again.';
  }
  if (/AADSTS50020|AADSTS90072/.test(detail)) {
    return 'Use your Team Today Microsoft account. Accounts from other organisations cannot sign in here.';
  }
  if (code === 'access_denied' || /AADSTS50105/.test(detail)) {
    return 'Microsoft did not allow this sign-in. Ask an administrator to give your account access to Team Today Admin.';
  }
  return 'Sign-in failed. Try again, and if it keeps happening, ask an administrator.';
};

const Login = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: checkingAccess, accessDenied, error: accessError, reload } = usePermissions();
  const [signingIn, setSigningIn] = useState(false);
  const signedIn = accounts.length > 0;
  // ProtectedRoute sends the page that was asked for; a failed sign-in adds what went wrong
  const from = location.state?.from;
  const signInError = location.state?.signInError;

  // Signed in and allowed in: carry on to the page that sent you here, or Home. This also moves
  // anyone already signed in off the login page.
  useEffect(() => {
    if (!signedIn || checkingAccess || accessDenied || accessError) return;
    const destination = from?.pathname && from.pathname !== '/login'
      ? { pathname: from.pathname, search: from.search || '', hash: from.hash || '' }
      : '/';
    navigate(destination, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, checkingAccess, accessDenied, accessError]);

  const handleLogin = async () => {
    setSigningIn(true);
    try {
      // On success the new account triggers the access check, and the effect above moves on
      await instance.loginPopup({ scopes: SCOPES });
    } catch (error) {
      console.error('Login failed:', error);
      // App swaps the whole router for a spinner while the popup is open, so this page has
      // unmounted by now; the message travels in the location state instead of component state
      navigate('/login', {
        replace: true,
        state: { from, signInError: { code: error?.errorCode || null, message: signInMessage(error) } },
      });
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = () => {
    // Drop any earlier sign-in error, but keep the page to return to after the next sign-in
    navigate('/login', { replace: true, state: { from } });
    instance.logoutPopup().catch((err) => console.error('Sign out failed:', err));
  };

  let content;
  if (!signedIn) {
    content = (
      <>
        <Button variant="primary" size="lg" className="w-100" onClick={handleLogin} disabled={signingIn}>
          Sign in with Microsoft
        </Button>
        {signInError && !signingIn && (
          <Alert variant="danger" className="mt-3 mb-0">{signInError.message}</Alert>
        )}
      </>
    );
  } else if (accessDenied) {
    content = (
      <>
        <Alert variant="warning">This account does not have access to Team Today Admin.</Alert>
        <p className="text-muted small text-break">
          Signed in as {accounts[0].username}. Sign out and use another account, or ask an administrator to add this one.
        </p>
        <Button variant="outline-secondary" className="w-100" onClick={handleSignOut}>Sign out</Button>
      </>
    );
  } else if (accessError) {
    content = (
      <>
        <Alert variant="danger">Could not check your access to Team Today Admin.</Alert>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="primary" onClick={reload}>Try again</Button>
          <Button variant="outline-secondary" onClick={handleSignOut}>Sign out</Button>
        </div>
      </>
    );
  } else {
    content = <LoadingState label="Checking your access" className="py-2" />;
  }

  return (
    // Signed in, the navigation bar sits above this card, so it no longer needs the full height
    <Container fluid className="d-flex align-items-center justify-content-center" style={{ minHeight: signedIn ? '60vh' : '100vh' }}>
      <Row className="w-100">
        <Col md={6} lg={4} className="mx-auto">
          <Card className="shadow-sm">
            <Card.Body className="p-4 p-sm-5">
              <h1 className="h3 text-center mb-3">Team Today Admin</h1>
              <p className="text-center text-muted mb-4">
                Sign in with your Team Today Microsoft account.
              </p>
              {content}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
